<template>
  <div>
    <div class="domainContainer">
      <p class="sctn-labels ion-no-margin">Button title (Max 16 characters)</p>
      <div class="counter-wrapper">
        <VTextField type="text" :modelValue="title" readonly :maxlength="16" placeholder="Button title" aria-label="Button title" appearance="ghost" class="vivid-input"></VTextField>
        <div class="custom-counter">
            {{ (title || '').length }}/16
        </div>
      </div>
    </div>
    <div v-if="isDynamicUrl" class="domainContainer"> 
      <!-- Dynamic URL  -->
      <p class="sctn-labels ion-no-margin">Button action (Open a webpage)</p>
      <div class="pre_text_cont">
        <span class="pre_text" :title="urlLabelToShow" style="white-space: nowrap;">{{ urlLabelToShow }}</span>
        <custom-field-input v-model="urlParam" placeholder="Enter URL here..." class="merged-url-input no-left-radius" @inputChange="buttonObjectHandler" />
      </div>
    </div>
    <div v-else-if="clickedButton.sub_type == 'copy_code'" class="domainContainer">
      <VSelect v-model="couponType" aria-label="copy_code" appearance="ghost" class="vivid-input marginBottom" fixed-dropdown>
        <VOption value="static" text="Static" />
        <VOption value="dynamic" text="Dynamic" />
      </VSelect>
      <p class="sctn-labels ion-no-margin">Enter a coupon code {{ couponType == 'static' ? '(Max 15 characters)' : '' }}</p>
      <div v-if="couponType == 'static'" class="counter-wrapper">
        <VTextField :modelValue="couponCode" type="text" :maxlength="15" @blur="couponCodeHandler" placeholder="Enter a coupon code" aria-label="Enter a coupon code" appearance="ghost" class="vivid-input"></VTextField>
        <div class="custom-counter">
            {{ (couponCode || '').length }}/15
        </div>
      </div>
      <div v-else-if="couponType == 'dynamic'" class="counter-wrapper">
        <CustomDataFieldPicker v-model="couponCode" :canAddNew="true" @change="onCustomFieldSelected"></CustomDataFieldPicker>
      </div>
    </div>
    <div v-else class="domainContainer">
      <p class="sctn-labels ion-no-margin">Button action (Open a webpage)</p>
      <VTextField type="text" :modelValue="url" readonly aria-label="webpage url" appearance="ghost" class="vivid-input"></VTextField>
    </div>
  </div>
</template>

<script>
import CustomDataFieldPicker from '/src/components/CustomDataFieldPicker.vue';
import CustomFieldInput from '@/components/CustomFieldInput.vue';
import { useUIStore } from '@/stores/ui';

export default {
  components: { CustomDataFieldPicker, CustomFieldInput },
  data() {
    return {
      urlParam: "",
      updatedButtonObject: {},
      couponCode: "",
      updatedCouponCodeButtonObject: {},
      couponType: "static", // Default to static coupon code
    }
  },
  props: {
    clickedButton: {
      type: Object,
      default: () => { return {}; }
    }
  },
  mounted() {
    if (this.isDynamicUrl) {
      this.urlParam = (this.clickedButton?.parameters?.length && this.clickedButton?.parameters[0].text) || '';
    }
    else if(this.clickedButton.sub_type == "copy_code") {
      let rawCouponCode = (this.clickedButton?.parameters?.length && this.clickedButton?.parameters[0].coupon_code) || '';
      
      // Extract the field name from <custom_field_fieldname> format for dynamic type
      if (rawCouponCode.startsWith('<custom_field_') && rawCouponCode.endsWith('>')) {
        this.couponCode = rawCouponCode.slice(14, -1); // Remove <custom_field_ and >
      } else {
        this.couponCode = rawCouponCode;
      }
      
      // set couponType based on parameter (default to static if not present)
      this.couponType = (this.clickedButton?.parameters?.length && this.clickedButton?.parameters[0].coupon_type) || 'static';
      // set initial object
      this.updatedCouponCodeButtonObject = {
        ...this.clickedButton,
        parameters: [{
          type: "coupon_code",
          coupon_code: this.couponType === 'dynamic' ? `<custom_field_${this.couponCode}>` : this.couponCode,
          coupon_type: this.couponType
        }]
      };
    }
  },
  computed: {
    urlLabelToShow() {
      return this.clickedButton?.url?.replace("{{1}}", "") || "";
    },
    isDynamicUrl() {
      return this.clickedButton?.url?.includes("{{1}}");
    },
    title() {
      return this.clickedButton.text || this.clickedButton.title;
    },
    url() {
      return this.clickedButton.url || this.clickedButton.web_url;
    }
  },
  watch: {
    couponType(newVal, oldVal) {
      // Reset coupon code when type changes
      this.couponCode = '';
      this.updatedCouponCodeButtonObject = {};
    },
    clickedButton: {
      handler(newVal) {
        if (this.isDynamicUrl) {
          this.urlParam = (newVal?.parameters?.length && newVal.parameters[0].text) || '';
        } else if (newVal.sub_type === "copy_code") {
          let rawCouponCode = (newVal?.parameters?.length && newVal.parameters[0].coupon_code) || '';
          
          // Extract the field name from <custom_field_fieldname> format for dynamic type
          if (rawCouponCode.startsWith('<custom_field_') && rawCouponCode.endsWith('>')) {
            this.couponCode = rawCouponCode.slice(14, -1); // Remove <custom_field_ and >
          } else {
            this.couponCode = rawCouponCode;
          }
          
          this.couponType = (newVal?.parameters?.length && newVal.parameters[0].coupon_type) || 'static';
          this.updatedCouponCodeButtonObject = {
            ...newVal,
            parameters: [{
              type: "coupon_code",
              coupon_code: this.couponType === 'dynamic' ? `<custom_field_${this.couponCode}>` : this.couponCode,
              coupon_type: this.couponType
            }]
          };
        }
      },
      immediate: true
    }
  },
  methods: {
    buttonObjectHandler() {
      this.updatedButtonObject = { ...this.clickedButton };
      this.updatedButtonObject.parameters = [
        {
          "type": "text",
          "text": this.urlParam
        }
      ]
    },
    couponCodeHandler(event) {
      if (this.couponType === 'dynamic') {
        this.couponCode = event.target.value;
        this.updatedCouponCodeButtonObject = { ...this.clickedButton };
        this.updatedCouponCodeButtonObject.parameters = [
          {
            type: "coupon_code",
            coupon_code: `<custom_field_${this.couponCode}>`,
            coupon_type: this.couponType
          }
        ];
        return;
      }
      const value = event.target.value;
      const isValid = /^[a-zA-Z0-9]*$/.test(value); // Only alphanumeric

      if (isValid) {
        this.couponCode = value;
        this.updatedCouponCodeButtonObject = { ...this.clickedButton };
        this.updatedCouponCodeButtonObject.parameters = [
          {
            type: "coupon_code",
            coupon_code: value,
            coupon_type: this.couponType
          }
        ];
      } else {
        this.updatedCouponCodeButtonObject = {};
        this.couponCode = '';
        return useUIStore().updateToast({
          open: true,
          message: "Please enter up to 15 alphanumeric characters only!",
          type: "danger"
        });
      }
    },
    onCustomFieldSelected(val) {
      this.couponCode = val;
      // Update the coupon code button object when custom field is selected
      this.updatedCouponCodeButtonObject = { ...this.clickedButton };
      this.updatedCouponCodeButtonObject.parameters = [
        {
          type: "coupon_code",
          coupon_code: `<custom_field_${val}>`,
          coupon_type: this.couponType 
        }
      ];
    }
  }
}
</script>

<style scoped>

.domainContainer{
  padding-bottom: 10px;
}
.sub-title{
  font-size: 14px;
  color: var(--gray-04);
  font-weight: 400;
  line-height: 16px;
}
.pre_text_cont{
  display: flex;
  align-items: center;
  border-radius: 5px;
  border: 1px solid var(--gray-03);
}
.pre_text_cont .pre_text {
  border-radius: 5px 0px 0px 5px;
  text-align: center;
  background: var(--gray-01);
  border: none;
  padding: 10px;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}
.pre_text_cont .merged-url-input {
  flex: 1;
  min-width: 0;
}
ion-input.sctn-input-currency{
  background: var(--background-primary);
  border-radius: 0px 5px 5px 0px;
  --padding-start: 10px;
}
.no-left-radius :deep(input) {
  border-radius: 0px 5px 5px 0px !important;
}
.no-left-radius :deep(ion-input) {
  border-radius: 0px 5px 5px 0px !important;
}
</style>